import { useT, type TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';

/**
 * Translates raw DB/domain enums into user-facing language and a colour
 * tone. Never render a raw enum like 'PICKING' or 'AWAITING_DISPOSITION'
 * directly in a table cell - always through this. Labels themselves live in
 * the i18n dictionary (`status.<ENUM_VALUE>`); this file only maps an enum
 * value to ITS key and a colour tone, never to text directly.
 */
const LABEL_KEYS: Record<string, TranslationKey> = {
  DRAFT: 'status.DRAFT',
  BUILDING: 'status.BUILDING',
  PICKING: 'status.PICKING',
  PICKED: 'status.PICKED',
  PACKING: 'status.PACKING',
  PACKED: 'status.PACKED',
  READY: 'status.READY',
  SHIPPED: 'status.SHIPPED',
  CANCELLED: 'status.CANCELLED',
  CANCELLED_AFTER_PICK: 'status.CANCELLED_AFTER_PICK',
  NEW: 'status.NEW',
  RESERVED: 'status.RESERVED',
  ARRIVED: 'status.ARRIVED',
  RECEIVING: 'status.RECEIVING',
  RECEIVED: 'status.RECEIVED',
  CLOSED: 'status.CLOSED',
  ACTIVE: 'status.ACTIVE',
  SUSPENDED: 'status.SUSPENDED',
  REVOKED: 'status.REVOKED',
  DISABLED: 'status.DISABLED',
  UNDECIDED: 'status.UNDECIDED',
  NONE: 'status.NONE',
  SHORT: 'status.SHORT',
  OVER: 'status.OVER',
  DAMAGED: 'status.DAMAGED',
  MISMATCH: 'status.MISMATCH',
  RESTOCK: 'status.RESTOCK',
  QUARANTINE: 'status.QUARANTINE',
  WRITE_OFF: 'status.WRITE_OFF',
  AWAITING_DISPOSITION: 'status.AWAITING_DISPOSITION',
  ON_HOLD: 'status.ON_HOLD',
  TERMINATED: 'status.TERMINATED',
  RECEIVING_DISCREPANCY: 'status.RECEIVING_DISCREPANCY',
  RETURN_AWAITING_DISPOSITION: 'status.RETURN_AWAITING_DISPOSITION',
  IMPORT_CONFLICT: 'status.IMPORT_CONFLICT',
  BILLING_UNPRICED: 'status.BILLING_UNPRICED',
  SHIPMENT_INCOMPLETE: 'status.SHIPMENT_INCOMPLETE',
  NO_TARIFF_RULE: 'status.NO_TARIFF_RULE',
  // FBS EXECUTION epic - SLA priority states (packages/core/src/model/sla.ts)
  normal: 'status.normal',
  approaching_cutoff: 'status.approaching_cutoff',
  urgent: 'status.urgent',
  blocked: 'status.blockedProblem',
  overdue: 'status.overdue',
  // blocker types
  SHORT_PICK: 'status.SHORT_PICK',
  MISSING_MARKING: 'status.MISSING_MARKING',
  MISSING_LABEL: 'status.MISSING_LABEL',
  INCOMPLETE_PACKAGE: 'status.INCOMPLETE_PACKAGE',
  CANCELLATION: 'status.CANCELLATION',
  DEADLINE_CHANGED: 'status.DEADLINE_CHANGED',
  // onboarding readiness.status
  NOT_STARTED: 'status.NOT_STARTED',
  IN_PROGRESS: 'status.IN_PROGRESS',
  BLOCKED: 'status.BLOCKED',
  READY_FOR_REVIEW: 'status.READY_FOR_REVIEW',
  // stock_movement.reason values
  RECEIPT: 'status.RECEIPT',
  PICK: 'status.PICK',
  PACK: 'status.PACK',
  PUTAWAY: 'status.PUTAWAY',
  SHIP: 'status.SHIP',
  MOVE: 'status.MOVE',
  RETURN_RECEIPT: 'status.RETURN_RECEIPT',
  CORRECTION: 'status.CORRECTION',
  // inbound_shipment.kind
  SUPPLY: 'status.SUPPLY',
  RETURN: 'status.RETURN',
  TRANSFER_IN: 'status.TRANSFER_IN',
  FOUND: 'status.FOUND',
  // inbound_line.responsible_party
  WAREHOUSE: 'status.WAREHOUSE',
  OWNER: 'status.OWNER',
  CARRIER: 'status.CARRIER',
  CHANNEL: 'status.CHANNEL',
  // evidence.kind
  DAMAGE: 'status.DAMAGE',
  UNKNOWN_ITEM: 'status.UNKNOWN_ITEM',
  RETURN_INSPECTION: 'status.RETURN_INSPECTION',
  // billable_event.kind (service_catalog_item codes) - mirrors apps/portal's
  // own subset, same wording, so a charge reads the same in both apps
  RECEIVING_UNIT: 'status.RECEIVING_UNIT',
  RECEIVING_DOC: 'status.RECEIVING_DOC',
  PICKING_UNIT: 'status.PICKING_UNIT',
  PACKING_UNIT: 'status.PACKING_UNIT',
  PACKAGE_MATERIAL: 'status.PACKAGE_MATERIAL',
  PALLET_HANDLING: 'status.PALLET_HANDLING',
  STORAGE_LOCATION: 'status.STORAGE_LOCATION',
  RETURN_UNIT: 'status.RETURN_UNIT',
  MARKING_SCAN: 'status.MARKING_SCAN',
  RELABEL: 'status.RELABEL',
  DISPOSAL: 'status.DISPOSAL',
  MANUAL_HANDLING: 'status.MANUAL_HANDLING',
  // seller_portal_user.state
  PENDING: 'status.PENDING',
  // shipment package label/print status
  REQUESTED: 'status.REQUESTED',
  PRINTING: 'status.PRINTING',
  PRINTED: 'status.PRINTED',
  FAILED: 'status.FAILED',
  // inbound_shipment.state (packages/core/src/model/operations.ts INBOUND_STATES)
  EXPECTED: 'status.EXPECTED',
  // task.state (TASK_STATES)
  ASSIGNED: 'status.ASSIGNED',
  DONE: 'status.DONE',
  // shipment.state (SHIPMENT_STATES)
  DEPARTED: 'status.DEPARTED',
  ACCEPTED: 'status.ACCEPTED',
  REJECTED: 'status.REJECTED',
  // package.state (PACKAGE_STATES)
  OPEN: 'status.OPEN',
  // invoice.state - mirrors apps/portal's own subset, same wording, so an
  // invoice status reads the same in both apps (Ops Invoices tab was
  // showing these raw - "issued"/"void" - never through this map at all)
  ISSUED: 'status.ISSUED',
  PAID: 'status.PAID',
  PARTIALLY_PAID: 'status.PARTIALLY_PAID',
  OVERDUE: 'status.OVERDUE',
  SENT: 'status.SENT',
  DISPUTED: 'status.DISPUTED',
  VOID: 'status.VOID',
};

const TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  SHIPPED: 'ok', RECEIVED: 'ok', CLOSED: 'ok', ACTIVE: 'ok', RESTOCK: 'ok', READY: 'ok',
  CANCELLED: 'danger', CANCELLED_AFTER_PICK: 'danger', REVOKED: 'danger', DISABLED: 'danger',
  SHORT: 'danger', OVER: 'warn', DAMAGED: 'danger', MISMATCH: 'danger', WRITE_OFF: 'warn',
  AWAITING_DISPOSITION: 'warn', SUSPENDED: 'warn', ON_HOLD: 'warn', UNDECIDED: 'warn',
  DRAFT: 'neutral', BUILDING: 'neutral', NONE: 'neutral', NEW: 'neutral',
  normal: 'ok', approaching_cutoff: 'warn', urgent: 'warn', blocked: 'danger', overdue: 'danger',
  SHORT_PICK: 'danger', MISSING_MARKING: 'warn', MISSING_LABEL: 'warn', INCOMPLETE_PACKAGE: 'warn',
  CANCELLATION: 'danger', DEADLINE_CHANGED: 'warn',
  PENDING: 'neutral', REQUESTED: 'neutral', PRINTING: 'neutral', PRINTED: 'ok', FAILED: 'danger',
  EXPECTED: 'neutral', ASSIGNED: 'neutral', DONE: 'ok',
  DEPARTED: 'ok', ACCEPTED: 'ok', REJECTED: 'danger', OPEN: 'neutral',
  ISSUED: 'warn', PAID: 'ok', PARTIALLY_PAID: 'warn', OVERDUE: 'danger',
  SENT: 'warn', DISPUTED: 'danger', VOID: 'neutral',
};

export function statusLabel(t: TranslateFn, raw: string | null | undefined): string {
  if (!raw) return '—';
  const key = LABEL_KEYS[raw];
  return key ? t(key) : raw.toLowerCase().replace(/_/g, ' ');
}

export function StatusBadge({ value }: { value: string | null | undefined }): JSX.Element {
  const t = useT();
  if (!value) return <span className="badge neutral">—</span>;
  const tone = TONE[value] ?? 'neutral';
  return <span className={`badge ${tone}`}>{statusLabel(t, value)}</span>;
}
